import React, { useEffect, useState } from 'react';
import { getCurrentUser, getCookie, getUserCases } from '../../functions/utils';
import DropdownComponent from './DropdownComponent';

function buildTree(classifications, mappings) {
  const nodeById = {};

  for (const cl of classifications) {
    nodeById[cl.ID] = {
      id: cl.ID,
      code: cl.Code,
      description: cl.Description,
      parentId: cl.Parent,
      periodType: cl.PeriodType,
      children: [],
      accounts: [],
    };
  }

  const roots = [];
  for (const node of Object.values(nodeById)) {
    if (node.parentId && nodeById[node.parentId]) {
      nodeById[node.parentId].children.push(node);
    } else {
      roots.push(node);
    }
  }

  for (const m of mappings) {
    const node = nodeById[m.Classification];
    if (node) {
      node.accounts.push({
        code: m.GLAccountCode,
        description: m.GLAccountDescription,
      });
    }
  }

  return roots;
}

const TreeNode = ({ node, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);
  const hasChildren = node.children.length > 0 || node.accounts.length > 0;

  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '3px 0',
          cursor: hasChildren ? 'pointer' : 'default',
          fontWeight: depth < 2 ? 'bold' : 'normal',
          fontSize: depth === 0 ? '15px' : '13px',
        }}
        onClick={() => hasChildren && setCollapsed(!collapsed)}
      >
        {hasChildren && (
          <span style={{ width: '16px', display: 'inline-block', fontSize: '10px', color: '#666' }}>
            {collapsed ? '▶' : '▼'}
          </span>
        )}
        {!hasChildren && <span style={{ width: '16px', display: 'inline-block' }} />}
        <span style={{ color: '#555', marginRight: '8px', fontFamily: 'monospace' }}>{node.code}</span>
        <span>{node.description}</span>
        {node.accounts.length > 0 && (
          <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>
            ({node.accounts.length} account{node.accounts.length !== 1 ? 's' : ''})
          </span>
        )}
      </div>
      {!collapsed && (
        <>
          {node.accounts.length > 0 && (
            <div style={{ marginLeft: (depth + 1) * 20, marginBottom: '4px' }}>
              {node.accounts.map((acc, i) => (
                <div key={i} style={{ padding: '2px 0', fontSize: '12px', color: '#2a7ae2', display: 'flex', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', minWidth: '60px' }}>{acc.code}</span>
                  <span>{acc.description}</span>
                </div>
              ))}
            </div>
          )}
          {node.children
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((child) => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
        </>
      )}
    </div>
  );
};

const TestEnvironment = () => {
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [glSchemeCode, setGlSchemeCode] = useState('RGS versie 3.2');
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [structuurTree, setStructuurTree] = useState(null);
  const [loadingStructuur, setLoadingStructuur] = useState(false);
  const [structuurError, setStructuurError] = useState(null);
  const [structuurStats, setStructuurStats] = useState(null);
  const [bankProviders, setBankProviders] = useState([]);
  const [bankProvider, setBankProvider] = useState('');
  const [bankEntiteit, setBankEntiteit] = useState('');
  const [bankResult, setBankResult] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [userCases, setUserCases] = useState([]);
  const [bankConnected, setBankConnected] = useState(null);
  const [bankConnections, setBankConnections] = useState([]);
  const [bankYear, setBankYear] = useState(String(new Date().getFullYear()));
  const [bankSyncId, setBankSyncId] = useState(null);
  const [bankStatus, setBankStatus] = useState('');

  useEffect(() => {
    fetchDivisions();
    fetchBankProviders();
    getUserCases().then((cases) => { if (cases) setUserCases(cases); });
    completeBankAuthorization();
  }, []);

  useEffect(() => {
    if (bankEntiteit) loadBankConnections(bankEntiteit);
  }, [bankEntiteit]);

  async function loadBankConnections(entiteit) {
    const res = await fetch('/bank/connections?entiteit=' + encodeURIComponent(entiteit));
    if (res.ok) setBankConnections(await res.json());
  }

  async function syncBank(connection) {
    setBankSyncId(connection.id);
    setBankError(null);
    setBankStatus('');
    await fetch('/bank/resetStatus');
    const poll = setInterval(async () => {
      const updates = await (await fetch('/bank/fetchUpdates')).json();
      if (updates.length > 0) setBankStatus(updates[updates.length - 1].text);
    }, 1000);
    try {
      const currentUser = await getCurrentUser();
      const res = await fetch('/bank/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId: connection.id, entiteit: connection.entiteit, year: bankYear, user_id: Number(currentUser) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'HTTP ' + res.status);
    } catch (err) {
      setBankError(err.message);
    } finally {
      clearInterval(poll);
      const updates = await (await fetch('/bank/fetchUpdates')).json();
      if (updates.length > 0) setBankStatus(updates[updates.length - 1].text);
      setBankSyncId(null);
      loadBankConnections(connection.entiteit);
    }
  }

  async function completeBankAuthorization() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;
    window.history.replaceState({}, '', window.location.pathname);
    setBankLoading(true);
    try {
      const res = await fetch('/bank/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'HTTP ' + res.status);
      setBankConnected(data);
      loadBankConnections(data.entiteit);
    } catch (err) {
      setBankError(err.message);
    } finally {
      setBankLoading(false);
    }
  }

  async function fetchBankProviders() {
    try {
      const response = await fetch('/bank/providers');
      if (response.ok) {
        const data = await response.json();
        setBankProviders(data);
        if (data.length > 0) setBankProvider(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching bank providers:', err);
    }
  }

  async function connectBank() {
    setBankLoading(true);
    setBankError(null);
    setBankResult(null);
    try {
      const currentUser = await getCurrentUser();
      const res = await fetch('/bank/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: bankProvider, entiteit: bankEntiteit, user_id: Number(currentUser) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'HTTP ' + res.status);
      setBankResult(data);
    } catch (err) {
      setBankError(err.message);
    } finally {
      setBankLoading(false);
    }
  }

  async function fetchDivisions() {
    try {
      const currentUser = await getCurrentUser();
      const response = await fetch('/exactonline/divisions?divisionId=' + getCookie('current_division') + '&currentUser=' + currentUser);
      if (response.ok) {
        const data = await response.json();
        setDivisions(data);
      }
    } catch (err) {
      console.error('Error fetching divisions:', err);
    }
  }

  function handleSelect(selectedOption) {
    if (selectedOption.type === 'administration') {
      setDivisionId(selectedOption.value);
    }
  }

  async function fetchMappingStructure() {
    setLoading(true);
    setError(null);
    setTree(null);
    setStats(null);
    try {
      const currentUser = await getCurrentUser();
      const division = divisionId || getCookie('current_division');

      // 1. Fetch mappings first to get the GLSchemeID
      const mappingParams = new URLSearchParams({ divisionId: division, currentUser });
      if (glSchemeCode) mappingParams.append('glSchemeCode', glSchemeCode);

      const mappingRes = await fetch('/exactonline/classificationMappings?' + mappingParams.toString());
      if (!mappingRes.ok) throw new Error('Mappings: HTTP ' + mappingRes.status);
      const mappingData = await mappingRes.json();
      const mappings = mappingData.results || [];
      
      if (mappings.length === 0) {
        setError('No mappings found for this division/scheme');
        return;
      }

      // 2. Use GLSchemeID from mappings to fetch the correct classification tree
      console.log(mappings[0]);
      const glSchemeId = mappings[0].GLSchemeID;
      const classParams = new URLSearchParams({ divisionId: division, currentUser, taxonomyNamespaceId: glSchemeId });

      const classRes = await fetch('/exactonline/glclassifications?' + classParams.toString());
      if (!classRes.ok) throw new Error('GLClassifications: HTTP ' + classRes.status);
      const classData = await classRes.json();
      const classifications = classData.results || [];
      console.log("Classification");
      console.log(classData);
      console.log("Mapping");
      console.log(mappingData);
      const roots = buildTree(classifications, mappings);
      setTree(roots);
      setStats({ classifications: classifications.length, mappings: mappings.length });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchMappingStructuur() {
    setLoadingStructuur(true);
    setStructuurError(null);
    setStructuurTree(null);
    setStructuurStats(null);
    try {
      const currentUser = await getCurrentUser();
      const division = divisionId || getCookie('current_division');
      const params = new URLSearchParams({ divisionId: division, currentUser });

      const res = await fetch('/exactonline/mappingstructuur?' + params.toString());
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      console.log('Mapping Structuur:', data);

      const allClassifications = [];
      const allMappings = data.mappings || [];
      let schemeCount = 0;

      for (const [schemeId, scheme] of Object.entries(data.schemes || {})) {
        schemeCount++;
        for (const cl of scheme.classifications || []) {
          allClassifications.push(cl);
        }
      }

      const roots = buildTree(allClassifications, allMappings);
      setStructuurTree(roots);
      setStructuurStats({ classifications: allClassifications.length, mappings: allMappings.length, schemes: schemeCount });
    } catch (err) {
      setStructuurError(err.message);
    } finally {
      setLoadingStructuur(false);
    }
  }

  return (
    <div style={{ padding: '20px' }}>
      <h3>Mapping Structure</h3>
      <div style={{ marginBottom: '10px' }}>
        <p>Administration</p>
        <DropdownComponent
          options={divisions.map((division) => ({ label: division.Description, value: division.Code, type: 'administration' }))}
          defaultValue={{ label: 'Select Administration', value: null, type: null }}
          onSelect={handleSelect}
          className="administration"
        />
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label>GL Scheme Code: </label>
        <input
          value={glSchemeCode}
          onChange={(e) => setGlSchemeCode(e.target.value)}
          style={{ marginLeft: '5px', width: '200px' }}
        />
      </div>
      <button className="import-btn hover" onClick={fetchMappingStructure} disabled={loading}>
        {loading ? 'Loading...' : 'Fetch GL Scheme Code'}
      </button>

      {error && <p style={{ color: 'red' }}>Error: {error}</p>}

      {stats && (
        <p style={{ margin: '10px 0', fontSize: '13px', color: '#666' }}>
          {stats.classifications} classifications, {stats.mappings} GL account mappings
        </p>
      )}

      {tree && (
        <div style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '4px', padding: '12px', maxHeight: '70vh', overflow: 'auto' }}>
          {tree
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((root) => (
              <TreeNode key={root.id} node={root} depth={0} />
            ))}
        </div>
      )}
      <hr style={{ margin: '20px 0' }} />

      <h3>Mapping Structuur (All Schemes)</h3>
      <button className="import-btn hover" onClick={fetchMappingStructuur} disabled={loadingStructuur}>
        {loadingStructuur ? 'Loading...' : 'Fetch Mapping Structuur'}
      </button>

      {structuurError && <p style={{ color: 'red' }}>Error: {structuurError}</p>}

      {structuurStats && (
        <p style={{ margin: '10px 0', fontSize: '13px', color: '#666' }}>
          {structuurStats.schemes} scheme(s), {structuurStats.classifications} classifications, {structuurStats.mappings} GL account mappings
        </p>
      )}

      {structuurTree && (
        <div style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '4px', padding: '12px', maxHeight: '70vh', overflow: 'auto' }}>
          {structuurTree
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((root) => (
              <TreeNode key={root.id} node={root} depth={0} />
            ))}
        </div>
      )}
      <hr style={{ margin: '20px 0' }} />

      <h3>Connect Bank</h3>
      <div style={{ marginBottom: '10px' }}>
        <label>Provider: </label>
        <select value={bankProvider} onChange={(e) => setBankProvider(e.target.value)} style={{ marginLeft: '5px' }}>
          {bankProviders.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <p>Case</p>
        <DropdownComponent
          options={userCases.map((c) => ({ label: c.name, value: c.id, type: 'case' }))}
          defaultValue={{ label: 'Select Case', value: null, type: null }}
          onSelect={(o) => o.type === 'case' && setBankEntiteit(`${o.value}_1`)}
          className="case"
        />
      </div>
      <button className="import-btn hover" onClick={connectBank} disabled={bankLoading || !bankProvider || !bankEntiteit}>
        {bankLoading ? 'Connecting...' : 'Connect Bank'}
      </button>

      {bankError && <p style={{ color: 'red' }}>Error: {bankError}</p>}

      {bankConnected && (
        <p style={{ margin: '10px 0', fontSize: '13px' }}>
          Connection {bankConnected.id} authorized: {bankConnected.accounts.map((a) => a.iban).join(', ')}
        </p>
      )}

      {bankConnections.length > 0 && (
        <div style={{ margin: '15px 0' }}>
          <label>Sync from year: </label>
          <input value={bankYear} onChange={(e) => setBankYear(e.target.value)} style={{ marginLeft: '5px', width: '60px' }} />
          {bankConnections.map((c) => (
            <div key={c.id} style={{ margin: '8px 0', fontSize: '13px' }}>
              Connection {c.id} ({c.provider}, status {c.status}, last sync {c.last_sync || 'never'}){' '}
              <button className="import-btn hover" onClick={() => syncBank(c)} disabled={bankSyncId !== null || c.status !== 1}>
                {bankSyncId === c.id ? 'Syncing...' : 'Sync'}
              </button>
            </div>
          ))}
          {bankStatus && <p style={{ fontSize: '13px', color: '#666' }}>{bankStatus}</p>}
        </div>
      )}

      {bankResult && (
        <p style={{ margin: '10px 0', fontSize: '13px' }}>
          Connection {bankResult.connectionId}:{' '}
          <a href={bankResult.url} target="_blank" rel="noopener noreferrer">Open bank authorization</a>
        </p>
      )}
    </div>
  );
};

export default TestEnvironment;
